// src/pages/ChampionGuide.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Play, Pause, RotateCcw, Clock, ChevronDown } from "lucide-react";

/* ---------- utils ---------- */
const fmt = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const ss = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${ss}`;
};
const norm = (s) => (s ?? "").toString().toLowerCase().replace(/[^a-z0-9]/g, "");

/* ---------- Aurora theme ---------- */
const AURORA_BG = {
    backgroundColor: "#0b1220",
    backgroundImage: `
    radial-gradient(1200px 800px at -20% -10%, rgba(14,165,233,.25), transparent 60%),
    radial-gradient(1200px 800px at 120% 0%, rgba(34,211,238,.22), transparent 55%),
    radial-gradient(1000px 800px at 110% 120%, rgba(168,85,247,.18), transparent 55%),
    radial-gradient(800px 600px at 0% 120%, rgba(16,185,129,.16), transparent 55%)
  `,
};
const CARD = "rounded-2xl border border-white/10 bg-white/[.05] backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,.25)]";

/* ---------- chips & badges ---------- */
const LABEL_COLORS = {
    skill: "bg-violet-400/10 text-violet-200 border-violet-400/30",
    item: "bg-sky-400/10 text-sky-200 border-sky-400/30",
    rune: "bg-amber-400/10 text-amber-200 border-amber-400/30",
    objective: "bg-emerald-400/10 text-emerald-200 border-emerald-400/30",
    ward: "bg-cyan-400/10 text-cyan-200 border-cyan-400/30",
    roam: "bg-teal-400/10 text-teal-200 border-teal-400/30",
    combo: "bg-rose-400/10 text-rose-200 border-rose-400/30",
    note: "bg-slate-400/10 text-slate-200 border-slate-400/30",
};
const TOKEN = {
    Q: "bg-violet-400/10 text-violet-200 border-violet-400/30",
    W: "bg-violet-400/10 text-violet-200 border-violet-400/30",
    E: "bg-violet-400/10 text-violet-200 border-violet-400/30",
    R: "bg-violet-400/10 text-violet-200 border-violet-400/30",
    FLASH: "bg-amber-400/10 text-amber-200 border-amber-400/30",
    IGNITE: "bg-rose-400/10 text-rose-200 border-rose-400/30",
    MYTHIC: "bg-sky-400/10 text-sky-200 border-sky-400/30",
    BOOTS: "bg-sky-400/10 text-sky-200 border-sky-400/30",
    DRAGON: "bg-emerald-400/10 text-emerald-200 border-emerald-400/30",
    HERALD: "bg-emerald-400/10 text-emerald-200 border-emerald-400/30",
    __OBJECT: "bg-slate-300/10 text-slate-100 border-slate-400/25",
};
const ResourceBadge = ({ r }) => {
    const raw = String(r ?? "").trim();
    if (!raw) return null;
    const upper = raw.toUpperCase().replace(/\s+/g, "");
    const m = upper.match(/^(\d+)?([A-Z_]+)$/);
    const qty = m?.[1] ?? "";
    const key = m?.[2] ?? upper;
    const style = TOKEN[key] ?? TOKEN[key.replace(/[0-9]/g, "")] ?? TOKEN.__OBJECT;
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[11px] font-semibold border ${style}`}>
      {qty}{key}
    </span>
    );
};
const withBadges = (t) =>
    String(t || "")
        .split(/(\[[^\]]+])/g)
        .filter(Boolean)
        .map((part, i) => {
            const m = part.match(/^\[([^\]]+)]$/);
            return m ? <ResourceBadge key={i} r={m[1]} /> : <span key={i} className="whitespace-pre">{part}</span>;
        });
const Chip = ({ type = "note", text = "" }) => {
    const cls = LABEL_COLORS[type] || LABEL_COLORS.note;
    return (
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-[12px] font-medium ${cls}`}>
            {withBadges(text)}
        </div>
    );
};
function chipToLabel(chip) {
    switch (chip.type) {
        case "skill":     return { type: "skill", text: `Level → [${chip.ability || "Q"}] ${chip.note ? `(${chip.note})` : ""}` };
        case "item":      return { type: "item", text: `Buy → [${(chip.slot || "MYTHIC").toUpperCase()}] ${chip.name || ""}` };
        case "rune":      return { type: "rune", text: `Rune → ${chip.name || ""}` };
        case "objective": return { type: "objective", text: `Objective → [${(chip.obj || "DRAGON").toUpperCase()}]` };
        case "ward":      return { type: "ward", text: `Ward → ${chip.where || "river"} (${chip.side || "bot"})` };
        case "roam":      return { type: "roam", text: `Roam → ${chip.to || "mid"}` };
        case "combo":     return { type: "combo", text: chip.text || "Combo" };
        case "note":      return { type: "note", text: chip.text || "Note" };
        default:          return { type: "note", text: "" };
    }
}

/* ---------- Data Dragon (generic champion) ---------- */
function useDataDragonChampion(championQuery) {
    const [ver, setVer] = useState(null);
    const [list, setList] = useState([]); // [{id,name,key}]
    const [champId, setChampId] = useState(null); // Data Dragon "id" (e.g., Annie, MissFortune, MonkeyKing)
    const [spells, setSpells] = useState([]);
    const [passive, setPassive] = useState(null);
    const [loading, setLoading] = useState(true);

    // fetch latest version + champion index
    useEffect(() => {
        let ok = true;
        (async () => {
            try {
                setLoading(true);
                const vs = await fetch("https://ddragon.leagueoflegends.com/api/versions.json").then(r => r.json());
                const v = vs?.[0];
                if (!v || !ok) return;
                setVer(v);
                const idx = await fetch(`https://ddragon.leagueoflegends.com/cdn/${v}/data/en_US/champion.json`).then(r => r.json());
                const arr = Object.values(idx?.data || {}).map(d => ({ id: d.id, name: d.name, key: d.key }));
                setList(arr);

                console.log(arr);

                // resolve query → Data Dragon id
                const q = norm(championQuery || "");
                const byExact = arr.find(d => norm(d.id) === q || norm(d.name) === q);
                // small alias fixes
                const aliases = {
                    wukong: "MonkeyKing",
                    nunuwillump: "Nunu",
                    chogath: "Chogath",
                    leblanc: "Leblanc",
                    kakashi: "Akshan", // playful example if you typo :)
                };
                const aliasHit = aliases[q];
                const resolved = byExact?.id || aliasHit || "Annie";
                setChampId(resolved || "Annie");
            } catch {
            } finally {
                if (ok) setLoading(false);
            }
        })();
        return () => { ok = false; };
    }, [championQuery]);

    // fetch specific champion details
    useEffect(() => {
        let ok = true;
        (async () => {
            if (!ver || !champId) return;
            try {
                setLoading(true);
                const one = await fetch(`https://ddragon.leagueoflegends.com/cdn/${ver}/data/en_US/champion/${champId}.json`).then(r => r.json());
                const data = one?.data?.[champId];
                if (!data || !ok) return;
                setSpells(data.spells || []);
                setPassive(data.passive || null);
            } catch {
            } finally {
                if (ok) setLoading(false);
            }
        })();
        return () => { ok = false; };
    }, [ver, champId]);

    const spellImg = (s) => ver && s?.image?.full ? `https://ddragon.leagueoflegends.com/cdn/${ver}/img/spell/${s.image.full}` : null;
    const passiveImg = (p) => ver && p?.image?.full ? `https://ddragon.leagueoflegends.com/cdn/${ver}/img/passive/${p.image.full}` : null;

    return { ver, list, champId, spells, passive, spellImg, passiveImg, loading };
}

/* ---------- guide data (Annie preset + generic fallback) ---------- */
const PLAN_ANNIE = {
    role: "Mid / Support",
    description:
        "Abuse Q last-hit mana refund, hold stun for threat, and look for Flash–Tibbers angles. Short trades pre-6; burst windows on item spikes.",
    steps: [
        { id: "s0", at: 0, time: "00:00", phase: "Early", title: "Prep stun & lane setup", chips: [
                { type: "item", slot: "BOOTS", name: "Doran's Ring + 2 Potions" },
                { type: "skill", ability: "Q", note: "start" },
                { type: "note", text: "Stack passive on fountain with [E] if safe" },
                { type: "ward", where: "river/raptors", side: "bot" },
            ]},
        { id: "s1", at: 90, time: "01:30", phase: "Early", title: "Level 2 trade", chips: [
                { type: "skill", ability: "W" },
                { type: "combo", text: "[Q] last-hit → walk up → [W] if stun prepped" },
                { type: "note", text: "Thin wave; keep stun threat" },
            ]},
        { id: "s2", at: 210, time: "03:30", phase: "Early", title: "Crash & reset window", chips: [
                { type: "ward", where: "pixel/river", side: "bot" },
                { type: "item", slot: "MYTHIC", name: "Lost Chapter component" },
                { type: "note", text: "Hold wave near tower vs gank" },
            ]},
        { id: "s3", at: 360, time: "06:00", phase: "Early", title: "Level 6 all-in threat", chips: [
                { type: "skill", ability: "R" },
                { type: "combo", text: "[FLASH] + [R] → [W] → [Q] [+IGNITE]" },
                { type: "note", text: "Ping jungler; keep stun prepped" },
            ]},
        { id: "s4", at: 540, time: "09:00", phase: "Mid", title: "First dragon / roam", chips: [
                { type: "objective", obj: "DRAGON" },
                { type: "roam", to: "bot after shove" },
                { type: "item", slot: "BOOTS", name: "Sorcerer's Shoes" },
            ]},
        { id: "s5", at: 900, time: "15:00", phase: "Mid", title: "2-item spike teamfights", chips: [
                { type: "item", slot: "MYTHIC", name: "Luden's / Liandry (matchup)" },
                { type: "item", name: "Shadowflame / Zhonya's [STOPWATCH]" },
                { type: "note", text: "Flank with stun; peel with [E] if needed" },
            ]},
        { id: "s6", at: 1500, time: "25:00", phase: "Late", title: "Baron setups & vision", chips: [
                { type: "ward", where: "Baron entrance", side: "top" },
                { type: "objective", obj: "HERALD" },
                { type: "combo", text: "Front-to-back: hold Tibbers to zone carries" },
            ]},
    ],
    runes: [
        { type: "rune", name: "Electrocute + Taste of Blood + Eyeball + Ultimate Hunter" },
        { type: "rune", name: "Secondary: Biscuit + Time Warp / Bone Plating" },
        { type: "note", text: "Aery for poke lanes; First Strike for scaling gold" },
    ],
    items: [
        { type: "item", slot: "MYTHIC", name: "Luden's (burst) / Liandry's (tanks)" },
        { type: "item", name: "Sorcerer's Shoes → Shadowflame / Zhonya's / Banshee's" },
        { type: "item", name: "Rabadon's if ahead; Morello vs heavy heal" },
    ],
    combos: [
        { type: "combo", text: "Pick: [FLASH] + [R] (stun) → [W] → [Q] [+IGNITE]" },
        { type: "combo", text: "Short trade: [E] → [Q] → step out" },
        { type: "combo", text: "Wave clear: [W] through wave → last-hit with [Q]" },
    ],
};
const GENERIC_PLAN = (name) => ({
    role: "Default Lane",
    description: `Solid, fundamentals-first plan for ${name}. Track wave states, ward on timers, and fight around key spell/CD spikes.`,
    steps: [
        { id: "g0", at: 0, time: "00:00", phase: "Early", title: "Opening & lane setup", chips: [
                { type: "item", name: "Starter + 2 Potions" },
                { type: "ward", where: "river/pixel", side: "bot" },
                { type: "note", text: "Crash first two waves unless heavy gank threat" },
            ]},
        { id: "g1", at: 180, time: "03:00", phase: "Early", title: "First reset window", chips: [
                { type: "item", name: "First components / boots" },
                { type: "note", text: "Fix wave before recall; hold TP if needed" },
            ]},
        { id: "g2", at: 360, time: "06:00", phase: "Early", title: "Level 6 threat", chips: [
                { type: "note", text: "Track jungler; shove & move for prio objects" },
            ]},
        { id: "g3", at: 900, time: "15:00", phase: "Mid", title: "Herald/Dragon setup", chips: [
                { type: "objective", obj: "DRAGON" },
                { type: "ward", where: "river entrances", side: "both" },
            ]},
        { id: "g4", at: 1500, time: "25:00", phase: "Late", title: "Baron & side lane", chips: [
                { type: "ward", where: "Baron quadrants", side: "top" },
                { type: "note", text: "Group on item/spell spikes; control vision" },
            ]},
    ],
    runes: [{ type: "rune", name: "Pick standard meta page for role" }],
    items: [{ type: "item", name: "Mythic → boots → 2nd spike" }],
    combos: [{ type: "note", text: "Add champ-specific combos later" }],
});

/* ---------- main ---------- */
export default function ChampionGuide() {
    const [champName, setChampName] = useState("Annie");
    const { list, champId, spells, passive, spellImg, passiveImg } = useDataDragonChampion(champName);

    // guide plan (use preset for Annie; generic for others)
    const PLAN = useMemo(() => {
        const id = champId ?? champName ?? "Annie";
        return norm(id) === "annie" ? PLAN_ANNIE : GENERIC_PLAN(id);
    }, [champId, champName]);

    // steps & timer state
    const steps = useMemo(() => [...(PLAN.steps || [])].sort((a, b) => (a.at || 0) - (b.at || 0)), [PLAN.steps]);
    const [timer, setTimer] = useState(0);
    const [running, setRunning] = useState(false);
    const [done, setDone] = useState(() => steps.map(() => false));
    useEffect(() => { setDone(steps.map(() => false)); setTimer(0); setRunning(false); }, [steps.length]);

    useEffect(() => {
        if (!running) return;
        const id = setInterval(() => setTimer((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, [running]);

    const lastElapsedIndex = useMemo(() => {
        let idx = -1;
        for (let i = 0; i < steps.length; i++) {
            if (timer >= (steps[i].at ?? 0)) idx = i; else break;
        }
        return idx;
    }, [steps, timer]);
    const currentIndex = useMemo(() => {
        if (running) return lastElapsedIndex >= 0 ? lastElapsedIndex : 0;
        const fi = done.findIndex((v) => !v);
        return fi === -1 ? steps.length - 1 : fi;
    }, [running, lastElapsedIndex, steps.length, done]);
    const current = steps[currentIndex];
    const next = steps[currentIndex + 1];
    const canPrev = currentIndex > 0;
    const canNext = currentIndex + 1 < steps.length;

    // chronological marking helpers
    const completeUpTo = (i) => setDone((_) => steps.map((__, idx) => idx <= i));
    const incompleteFrom = (i) => setDone((_) => steps.map((__, idx) => (idx < i ? true : false)));
    const gotoPrev = () => { if (canPrev) { setRunning(false); incompleteFrom(currentIndex); } };
    const gotoNext = () => { if (canNext) { setRunning(false); completeUpTo(currentIndex); } };
    const clickStep = (i) => (done[i] ? incompleteFrom(i) : completeUpTo(i));

    const PHASE_ORDER = ["Early", "Mid", "Late"];
    const phaseTitle = (p) => (p ? p.toUpperCase() : "");

    return (
        <div className="min-h-screen text-slate-200" style={AURORA_BG}>
            {/* Header */}
            <header className="sticky top-0 z-30 border-b border-white/10 backdrop-blur-md" style={{ background: "linear-gradient(to right, rgba(15,23,42,.65), rgba(15,23,42,.45))" }}>
                <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5">
                    <div className="flex items-center gap-3">
                        <div className="text-lg sm:text-xl font-extrabold tracking-tight">
                            {champId || champName} <span className="opacity-60">— Guide</span>
                        </div>
                        <div className="flex-1" />
                        <span className="font-mono text-sm px-2.5 py-1.5 rounded-xl border border-white/10 bg-white/5 flex items-center gap-1.5">
              <Clock className="w-4 h-4" /> {fmt(timer)}
            </span>
                        <button className="px-2.5 py-1.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 flex items-center gap-1"
                                onClick={() => setRunning((r) => !r)}>
                            {running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                            <span className="text-sm">{running ? "Pause" : "Start"}</span>
                        </button>
                        <button className="px-2.5 py-1.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 flex items-center gap-1"
                                onClick={() => { setTimer(0); setRunning(false); }}>
                            <RotateCcw className="w-4 h-4" /> <span className="text-sm">Reset</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="max-w-6xl mx-auto px-3 sm:px-4 py-6 space-y-6">
                {/* Champion selector */}
                <section className={`${CARD} p-3`}>
                    <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Champion</div>
                    <div className="relative">
                        <select
                            value={champName}
                            onChange={(e) => setChampName(e.target.value)}
                            className="appearance-none w-full bg-white/5 text-slate-100 border border-white/10 rounded-xl px-4 py-2 pr-10 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
                        >
                            {/* simple first load fallback */}
                            {!list.length && <option>Annie</option>}
                            {list
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((c) => (
                                    <option key={c.id} value={c.id} className="bg-slate-900">
                                        {c.name}
                                    </option>
                                ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                </section>

                {/* Overview */}
                <section className={`${CARD} p-4`}>
                    <h3 className="text-sm font-bold text-slate-100 mb-2">Overview</h3>
                    <p className="text-slate-300 text-sm leading-relaxed">{PLAN.description}</p>
                </section>

                {/* Spells quicklook */}
                <section className={`${CARD} p-4`}>
                    <h3 className="text-sm font-bold text-slate-100 mb-3">Spells & Passive</h3>
                    <div className="flex flex-wrap gap-4">
                        {passive && (
                            <div className="flex items-center gap-2">
                                {passiveImg(passive) && <img src={passiveImg(passive)} alt="Passive" className="w-8 h-8 rounded-md" />}
                                <div className="text-sm">
                                    <div className="font-semibold">Passive: {passive.name}</div>
                                    <div className="text-slate-400 text-xs truncate max-w-[260px]" title={passive.description?.replace(/<[^>]*>/g, "") || ""}>
                                        {(passive.description || "").replace(/<[^>]*>/g, "")}
                                    </div>
                                </div>
                            </div>
                        )}
                        {spells.map((s) => (
                            <div key={s.id} className="flex items-center gap-2">
                                {spellImg(s) && <img src={spellImg(s)} alt={s.name} className="w-8 h-8 rounded-md" />}
                                <div className="text-sm">
                                    <div className="font-semibold">{s.name}</div>
                                    <div className="text-slate-400 text-xs truncate max-w-[260px]" title={s.tooltip?.replace(/<[^>]*>/g, "") || ""}>
                                        {s.tooltip?.replace(/<[^>]*>/g, "") || ""}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* HUD: Current / Next + footer nav */}
                <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {current && (
                        <div className={`${CARD} p-4 ring-1 ring-emerald-400/10`}>
                            <div className="text-xs uppercase tracking-wide text-slate-400">Current</div>
                            <div className="flex items-center justify-between mt-1 mb-3">
                                <div className="font-mono text-2xl">{current.time || fmt(current.at || 0)}</div>
                            </div>
                            <div className="text-sm font-semibold mb-2">{current.title || "—"}</div>
                            <div className="flex flex-wrap gap-1.5">
                                {(current.chips || []).map((c, i) => {
                                    const l = chipToLabel(c);
                                    return <Chip key={i} type={l.type} text={l.text} />;
                                })}
                            </div>
                            <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between">
                                <div className="text-xs text-slate-400">Step {currentIndex + 1} / {steps.length}</div>
                                <div className="flex gap-2">
                                    <button onClick={gotoPrev} disabled={!canPrev}
                                            className={`px-3 py-1.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-sm ${!canPrev ? "opacity-40 cursor-not-allowed" : ""}`}>
                                        Previous
                                    </button>
                                    <button onClick={gotoNext} disabled={!canNext}
                                            className={`px-3 py-1.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-sm ${!canNext ? "opacity-40 cursor-not-allowed" : ""}`}>
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {next && (
                        <div className={`${CARD} p-4 ring-1 ring-cyan-400/10`}>
                            <div className="text-xs uppercase tracking-wide text-slate-400">Up Next</div>
                            <div className="flex items-center justify-between mt-1 mb-3">
                                <div className="font-mono text-2xl">{next.time || fmt(next.at || 0)}</div>
                            </div>
                            <div className="text-sm font-semibold mb-2">{next.title || "—"}</div>
                            <div className="flex flex-wrap gap-1.5">
                                {(next.chips || []).map((c, i) => {
                                    const l = chipToLabel(c);
                                    return <Chip key={i} type={l.type} text={l.text} />;
                                })}
                            </div>
                        </div>
                    )}
                </section>

                {/* Runes & Items */}
                <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className={`${CARD} p-4`}>
                        <h3 className="text-sm font-bold text-slate-100 mb-2">Runes</h3>
                        <div className="flex flex-wrap gap-1.5">
                            {(PLAN.runes || []).map((r, i) => <Chip key={i} type={r.type} text={r.name || r.text} />)}
                        </div>
                    </div>
                    <div className={`${CARD} p-4`}>
                        <h3 className="text-sm font-bold text-slate-100 mb-2">Items</h3>
                        <div className="flex flex-wrap gap-1.5">
                            {(PLAN.items || []).map((it, i) => <Chip key={i} type="item" text={it.name} />)}
                        </div>
                    </div>
                </section>

                {/* Steps grouped by phase */}
                <section className="space-y-6">
                    {(() => {
                        const by = new Map();
                        steps.forEach((s) => {
                            const key = s.phase || "Other";
                            if (!by.has(key)) by.set(key, []);
                            by.get(key).push(s);
                        });
                        const ordered = Array.from(by.keys()).sort((a, b) => {
                            const ia = PHASE_ORDER.indexOf(a), ib = PHASE_ORDER.indexOf(b);
                            if (ia === -1 && ib === -1) return a.localeCompare(b);
                            if (ia === -1) return 1;
                            if (ib === -1) return -1;
                            return ia - ib;
                        });
                        return ordered.map((phase) => {
                            const list = by.get(phase) || [];
                            return (
                                <div key={phase} className="space-y-3">
                                    <div className="text-xs tracking-widest font-bold text-slate-300/80 px-1">{phaseTitle(phase)}</div>
                                    <div className="grid grid-cols-1 gap-3">
                                        {list.map((s) => {
                                            const idx = steps.findIndex((x) => x.id === s.id);
                                            const isCurrent = current && current.id === s.id;
                                            const ring = isCurrent ? "ring-emerald-400/30" : "ring-transparent";
                                            return (
                                                <div
                                                    key={s.id}
                                                    className={`${CARD} p-4 ring-2 ${ring} ${done[idx] ? "opacity-60" : ""}`}
                                                    onClick={() => clickStep(idx)}
                                                >
                                                    <div className="flex items-start gap-4">
                                                        <div className="min-w-[72px] font-mono text-sm px-2 py-1 rounded-lg border border-white/10 bg-white/5">
                                                            {s.time || fmt(s.at || 0)}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-start justify-between gap-3">
                                                                <div className="text-sm font-semibold truncate">{s.title || "—"}</div>
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); clickStep(idx); }}
                                                                    className="text-xs px-3 py-1.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10"
                                                                >
                                                                    {done[idx] ? "Undo" : "Mark complete"}
                                                                </button>
                                                            </div>
                                                            <div className="mt-2 flex flex-wrap gap-1.5">
                                                                {(s.chips || []).map((c, i) => {
                                                                    const l = chipToLabel(c);
                                                                    return <Chip key={i} type={l.type} text={l.text} />;
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        });
                    })()}
                </section>

                {/* Combos / Notes */}
                {(PLAN.combos?.length ?? 0) > 0 && (
                    <section className={`${CARD} p-4`}>
                        <h3 className="text-sm font-bold text-slate-100 mb-2">Combos & Tips</h3>
                        <div className="flex flex-wrap gap-1.5">
                            {PLAN.combos.map((c, i) => {
                                const l = chipToLabel(c);
                                return <Chip key={i} type={l.type} text={l.text} />;
                            })}
                        </div>
                    </section>
                )}
            </main>
        </div>
    );
}
